import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Loader2, Phone, Mail } from 'lucide-react';
import contactService from '../../services/contactService';
import '../../styles/admin/AboutPage.css';

const defaults = {
  public_email: 'contact@fitcoursehub.com',
  public_phone: '+1 (555) 123-4567',
  notification_email: 'updates@fitcoursehub.com',
};

export default function AdminContact() {
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await contactService.getAdminContact();
      setForm({ ...defaults, ...(data || {}) });
    } catch (error) {
      console.error('Failed to load contact settings', error);
      setStatus({ type: 'error', message: 'Could not load contact settings' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = await contactService.updateContact(form);
      setForm({ ...defaults, ...(data || form) });
      setStatus({ type: 'success', message: 'Contact settings updated' });
    } catch (error) {
      console.error('Save failed', error);
      setStatus({ type: 'error', message: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(null), 3000);
    }
  };

  const resetToDefault = () => {
    setForm(defaults);
    setStatus({ type: 'info', message: 'Reset to defaults (not saved yet)' });
  };

  return (
    <div className="admin-about">
      <div className="about-header">
        <div>
          <p className="eyebrow">Contact Page</p>
          <h1>Contact settings</h1>
          <p className="muted">Control the phone, public email, and where contact form updates are sent.</p>
        </div>
        <div className="header-actions">
          <button className="ghost-btn" onClick={resetToDefault}>
            <RefreshCw size={16} /> Reset
          </button>
          <button className="primary-btn" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {status && (
        <div className={`status-banner ${status.type}`}>
          {status.message}
        </div>
      )}

      {loading ? (
        <div className="about-card loading">
          <Loader2 className="spin" size={28} />
          <p>Loading settings...</p>
        </div>
      ) : (
        <>
          <div className="about-card">
            <div className="card-title">Public contact details</div>
            <div className="stack">
              <label className="field">
                <span>Email shown on contact page</span>
                <div className="field-hint">Visitors will see this email and can use it to reach out directly.</div>
                <div className="field-with-icon">
                  <Mail size={16} />
                  <input
                    type="email"
                    value={form.public_email}
                    onChange={(e) => handleChange('public_email', e.target.value)}
                    placeholder="contact@fitcoursehub.com"
                  />
                </div>
              </label>
              <label className="field">
                <span>Phone number shown on contact page</span>
                <div className="field-hint">Displayed to visitors; add country code.</div>
                <div className="field-with-icon">
                  <Phone size={16} />
                  <input
                    type="tel"
                    value={form.public_phone}
                    onChange={(e) => handleChange('public_phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </label>
            </div>
          </div>

          <div className="about-card">
            <div className="card-title">Notification routing</div>
            <div className="stack">
              <label className="field">
                <span>Where to send contact form updates</span>
                <div className="field-hint">This inbox will receive the messages submitted from the contact page.</div>
                <input
                  type="email"
                  value={form.notification_email}
                  onChange={(e) => handleChange('notification_email', e.target.value)}
                  placeholder="updates@fitcoursehub.com"
                />
              </label>
            </div>
          </div>

          <div className="footer-actions">
            <button className="ghost-btn" onClick={resetToDefault}>
              <RefreshCw size={16} /> Reset (not saved)
            </button>
            <button className="primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="spin" size={16} /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
